"use client";

import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import { cn } from "@/app/_module/lib/utils";
import {
  A2PVerificationData,
  BUSINESS_TYPES,
  US_STATES,
} from "@/app/_module/types/a2p.types";
import type { RegistrationType } from "./Step0RegistrationTypeSelect";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { Panel, Section } from "./overview/primitives";
import { Field, ReadOnlyField, StepIntro, controlClass } from "./overview/wizard-ui";

// Helper functions for phone number formatting
const formatPhoneForDisplay = (value: string): string => {
  if (!value) return '';

  // Extract only digits
  const digits = value.replace(/\D/g, '');

  // Remove leading 1 if present (handles +1xxxxxxxxxx or 1xxxxxxxxxx)
  // 11 digits starting with 1 = country code included
  const normalizedDigits = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  // Limit to 10 digits for US phone
  const limitedDigits = normalizedDigits.slice(0, 10);

  // Format as (xxx) xxx-xxxx
  if (limitedDigits.length === 0) return '';
  if (limitedDigits.length <= 3) return `(${limitedDigits}`;
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
};

const formatPhoneForBackend = (value: string): string => {
  if (!value) return '';

  // Extract only digits
  const digits = value.replace(/\D/g, '');

  // Remove leading 1 if present (handles +1xxxxxxxxxx or 1xxxxxxxxxx)
  const normalizedDigits = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  // Limit to 10 digits and add +1 prefix
  const limitedDigits = normalizedDigits.slice(0, 10);

  if (limitedDigits.length === 10) {
    return `+1${limitedDigits}`;
  }

  // Return as-is if not complete
  return limitedDigits;
};

interface Step2BusinessInfoProps {
  /** Kept for the wizard's call site; sole props use Step2SoleProprietorInfo instead. */
  registrationType?: RegistrationType | null;
}

export default function Step2BusinessInfo(_props: Step2BusinessInfoProps) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<A2PVerificationData>();
  const { branding } = useBranding();

  const businessType = watch("businessType");
  const isSoleProprietorship = businessType === "Sole Proprietorship";
  const input = (invalid: boolean) => cn("h-11", controlClass, invalid && "!border-red-700");

  return (
    <div className="space-y-8">
      <StepIntro title="Business details">
        <p>
          Enter them exactly as they appear on your IRS records. We send these details to the carriers&apos; registry
          to verify your business. All fields are required.
        </p>
      </StepIntro>

      <Section id="a2p-business" title="Business">
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field
            id="businessName"
            label="Legal business name"
            error={errors.businessName?.message}
            hint={
              isSoleProprietorship
                ? "Use your personal legal name, the one tied to your SSN. The IRS files sole proprietor EINs under the owner's name, so a trade name or DBA gets rejected."
                : "Exactly as on your CP 575, including LLC, Inc. or Corp. For example “Smith Insurance LLC”, not “Smith Insurance”. A mismatch gets the registration rejected."
            }
          >
            <Input
              {...register("businessName", {
                required: "Enter the legal business name",
                minLength: { value: 2, message: "Use at least 2 characters" },
                maxLength: { value: 100, message: "Use 100 characters or fewer" },
              })}
              id="businessName"
              placeholder={isSoleProprietorship ? "John Michael Smith" : "Your Business LLC"}
              aria-invalid={!!errors.businessName}
              className={input(!!errors.businessName)}
              onBlur={(e) => {
                // Auto-capitalize first letter of each word (Title Case)
                const val = e.target.value;
                if (val && val === val.toLowerCase()) {
                  const titleCase = val.replace(/\b\w/g, (c: string) => c.toUpperCase());
                  setValue('businessName', titleCase);
                }
              }}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="businessType" label="Business type" error={errors.businessType?.message}>
              <Controller
                name="businessType"
                control={control}
                rules={{ required: "Choose a business type" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="businessType" aria-invalid={!!errors.businessType} className={input(!!errors.businessType)}>
                      <SelectValue placeholder="Choose a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>

            <Field id="businessPhone" label="Business phone" error={errors.businessPhone?.message}>
              <Controller
                name="businessPhone"
                control={control}
                rules={{
                  required: "Enter the business phone",
                  validate: (value) => {
                    const digits = value?.replace(/\D/g, '') || '';
                    const normalizedDigits = digits.startsWith('1') && digits.length > 10
                      ? digits.slice(1)
                      : digits;
                    if (normalizedDigits.length !== 10) {
                      return "Enter a 10-digit phone number";
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  // Display value formatted as (xxx) xxx-xxxx
                  const displayValue = formatPhoneForDisplay(field.value || '');

                  return (
                    <Input
                      id="businessPhone"
                      type="tel"
                      inputMode="numeric"
                      placeholder="(555) 123-4567"
                      value={displayValue}
                      onChange={(e) => {
                        const input = e.target.value;
                        // Only allow digits to be typed
                        const newValue = input.replace(/[^\d]/g, '');

                        // Get existing digits
                        const existingDigits = (field.value || '').replace(/\D/g, '');
                        const normalizedExisting = existingDigits.startsWith('1') && existingDigits.length > 10
                          ? existingDigits.slice(1)
                          : existingDigits;

                        // If user is deleting, handle backspace
                        if (input.length < displayValue.length) {
                          // Remove the last digit
                          const updatedDigits = normalizedExisting.slice(0, -1);
                          field.onChange(formatPhoneForBackend(updatedDigits));
                        } else {
                          // User is adding digits
                          field.onChange(formatPhoneForBackend(newValue));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Allow only digits, backspace, delete, tab, arrows
                        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
                        if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      aria-invalid={!!errors.businessPhone}
                      className={cn(input(!!errors.businessPhone), "tabular-nums")}
                    />
                  );
                }}
              />
            </Field>
          </div>

          <Field id="businessEmail" label="Business email" error={errors.businessEmail?.message}>
            <Input
              {...register("businessEmail", {
                required: "Enter the business email",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Enter a valid email address",
                },
              })}
              id="businessEmail"
              type="email"
              placeholder="contact@yourbusiness.com"
              aria-invalid={!!errors.businessEmail}
              className={input(!!errors.businessEmail)}
            />
          </Field>

          {/* Website - locked for A2P compliance; the backend registers the business page. */}
          <ReadOnlyField
            label="Website"
            value="Set up for you"
            hint={`We register your ${branding.brandName} business page as your website, with our privacy policy and terms.`}
          />
        </Panel>
      </Section>

      <Section
        id="a2p-rep"
        title="Authorized representative"
        description="The person registering the business. Use your own name, not the business name."
      >
        <Panel className="grid gap-5 px-4 py-5 sm:grid-cols-2 sm:px-5">
          <Field id="authorizedRepFirstName" label="First name" error={errors.authorizedRepFirstName?.message}>
            <Input
              {...register("authorizedRepFirstName", {
                required: "Enter a first name",
              })}
              id="authorizedRepFirstName"
              placeholder="John"
              aria-invalid={!!errors.authorizedRepFirstName}
              className={input(!!errors.authorizedRepFirstName)}
            />
          </Field>
          <Field id="authorizedRepLastName" label="Last name" error={errors.authorizedRepLastName?.message}>
            <Input
              {...register("authorizedRepLastName", {
                required: "Enter a last name",
              })}
              id="authorizedRepLastName"
              placeholder="Smith"
              aria-invalid={!!errors.authorizedRepLastName}
              className={input(!!errors.authorizedRepLastName)}
            />
          </Field>
        </Panel>
      </Section>

      <Section id="a2p-address" title="Business address" description="The address the IRS has on file for the business.">
        <Panel className="space-y-5 px-4 py-5 sm:px-5">
          <Field id="street" label="Street address" error={errors.street?.message}>
            <Input
              {...register("street", {
                required: "Enter the street address",
              })}
              id="street"
              placeholder="123 Main Street"
              aria-invalid={!!errors.street}
              className={input(!!errors.street)}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="city" label="City" error={errors.city?.message}>
              <Input
                {...register("city", {
                  required: "Enter the city",
                })}
                id="city"
                placeholder="Detroit"
                aria-invalid={!!errors.city}
                className={input(!!errors.city)}
              />
            </Field>
            <Field id="state" label="State" error={errors.state?.message}>
              <Controller
                name="state"
                control={control}
                rules={{ required: "Choose a state" }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="state" aria-invalid={!!errors.state} className={input(!!errors.state)}>
                      <SelectValue placeholder="Choose a state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="zipCode" label="ZIP code" error={errors.zipCode?.message}>
              <Input
                {...register("zipCode", {
                  required: "Enter the ZIP code",
                  pattern: {
                    value: /^\d{5}(-\d{4})?$/,
                    message: "Use 12345 or 12345-6789",
                  },
                })}
                id="zipCode"
                inputMode="numeric"
                placeholder="12345"
                maxLength={10}
                aria-invalid={!!errors.zipCode}
                className={cn(input(!!errors.zipCode), "tabular-nums")}
              />
            </Field>
            {/* Registered so the value submits; texting registration is US-only. */}
            <input type="hidden" {...register("country")} />
            <ReadOnlyField label="Country" value="United States" hint="Texting registration is for US businesses only." />
          </div>
        </Panel>
      </Section>
    </div>
  );
}
